import {keycloak} from "../views/utility/KeycloakProvider";
import {
  TopicDescription,
  TopicDescriptionInfo,
  TopicDescriptionPushTransferResponse,
  ErrorReason,
  ApplicationDomain,
  ApplicationDomainCreate,
  Application,
  User,
  Connector,
  DataStream,
  RecentTopicDescription,
  ActiveTopicCount,
  OutgoingTopicDescription,
  TopicDescriptionMqttTransferResponse,
  DatasetDescription,
  TopicFolderContent,
  CreateDraftFolderResponse as CreateDraftFolderResponse,
  DraftFolderContent,
} from "./APITypes";
import {TileApplications} from "@spg/component_library";

/**
 * Enum for REST-request methods
 * @type
 */
type Method = "POST" | "GET" | "PATCH" | "PUT" | "DELETE";

/**
 * API-requests return a promise resolving to this type.
 * @type
 * @property success
 * @property httpCode – Missing if fetching hasn't finished
 * @property unresolved – Whether the request hasn't been made or resolved yet
 * @property errorReason - Missing if successful
 * @property payload - Missing if unsuccessful
 */
type Resolution<T> = {
  success: boolean;
  httpCode?: number;
  unresolved?: boolean;
  errorReason?: ErrorReason[] | string;
  payload?: T;
};

const externalBackends =
  process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test"
    ? {
        portal: "http://localhost:8000/portal/api/",
        file_store: "http://localhost:8004/",
        keycloak_sidecar: "http://localhost:8006/api/users",
      }
    : {
        portal: "/portal/api/",
        file_store: "/",
        keycloak_sidecar: "/api/users",
      };

/**
 * @namespace API
 * @desc Provides an interface to operations in the backend.
 */
const API = {
  url:
    process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test"
      ? "http://localhost:8002/datamarket/api/"
      : "/datamarket/api/",
  /**
   * Fetches a resource using the appropriate headers.
   * @function
   * @param path - relative path of the resource
   * @param method - the method by which the fetch is performed
   * @param withCredentials - whether to include the keycloak authorization header
   * @param body - an optional body to attach to the fetch
   * @param abortController - controller to handle aborting of requests
   * @param backend – specification of another backend
   */
  fetchWithHeaders: (
    path: string,
    method?: Method,
    withCredentials?: boolean,
    body?: object,
    abortController?: AbortController,
    backend?: keyof typeof externalBackends,
    additionalHeaders?: {name: string; value: string}[],
  ) => {
    const header: HeadersInit = new Headers();
    header.set("Content-Type", "application/json");
    if (withCredentials)
      header.set("Authorization", "Bearer " + keycloak.token);
    if (additionalHeaders)
      for (const h of additionalHeaders) header.set(h.name, h.value);
    const request: RequestInit = {
      method: method,
      headers: header,
      credentials: "same-origin",
      signal: abortController?.signal,
    };
    if (body) request.body = JSON.stringify(body);
    return fetch(
      (backend ? externalBackends[backend] : API.url) + encodeURI(path),
      request,
    );
  },
  /**
   * Evaluates whether a fetch succeeds.
   * @function
   * @param request - the fetched promise to evaluate
   * @param bodyAsSpecifics - whether to return the contents of the specifics-field
   * @returns a non-rejecting promise
   */
  resolveFetch: async <T>(
    request: Promise<Response>,
    bodyAsSpecifics?: boolean,
  ): Promise<Resolution<T>> => {
    try {
      const response = await request;
      if (response.ok) {
        const len = response.headers.get("content-length");
        if (!len || +len == 0)
          return {success: true, httpCode: response.status};
        try {
          const json = await response.json();
          if (
            bodyAsSpecifics &&
            typeof json === "object" &&
            json &&
            "specifics" in json
          )
            return {
              success: true,
              httpCode: response.status,
              payload: {...json, specifics: undefined, ...json["specifics"]},
            };
          return {
            success: true,
            httpCode: response.status,
            payload: json,
          };
        } catch (errorReason: unknown) {
          return {
            success: false,
            httpCode: response.status,
            errorReason:
              errorReason instanceof Error
                ? errorReason.message
                : String(errorReason),
          };
        }
      }
      const json = (await response.json()) as unknown;
      if (typeof json === "object" && json && "detail" in json) {
        if (
          typeof json.detail === "object" &&
          json.detail &&
          Array.isArray(json.detail) &&
          (json.detail as unknown[]).every(
            (d) => typeof d === "object" && d && "msg" in d,
          )
        )
          return {
            success: false,
            httpCode: response.status,
            errorReason: json.detail as ErrorReason[],
          };
        else
          return {
            success: false,
            httpCode: response.status,
            errorReason: json.detail as string,
          };
      } else
        return {
          success: false,
          httpCode: response.status,
          errorReason: response.statusText,
        };
    } catch (errorReason: unknown) {
      return {
        success: false,
        httpCode: 400,
        errorReason:
          errorReason instanceof Error
            ? errorReason.message
            : String(errorReason),
      };
    }
  },

  /**
   * Fetches a file access token for a topic.
   * @function
   * @param topicId - the ID of the topic
   * @returns a non-rejecting promise
   */
  withFileAccessToken: async <T>(
    topicId: string,
    fetcher: (token: string) => Promise<Resolution<T>>,
  ): Promise<Resolution<T>> => {
    const abortController = new AbortController();
    const tokenResponse = await API.getFileAccessToken(
      topicId,
      abortController,
    );
    if (!tokenResponse.success || !tokenResponse.payload)
      return tokenResponse as Resolution<never>;
    return fetcher(tokenResponse.payload.token);
  },

  /**
   * Attempts to create a topic description.
   * @function
   * @param topicDescription - the details about the topic description
   * @param bodyAsSpecifics - whether to return the contents of the specifics-field (for push-method or MQTT transfer type)
   */
  createTopicDescription: async (
    topicDescription: OutgoingTopicDescription,
    bodyAsSpecifics?: boolean,
  ): Promise<
    Resolution<
      | {id: string}
      | TopicDescriptionPushTransferResponse
      | TopicDescriptionMqttTransferResponse
    >
  > => {
    return await API.resolveFetch<
      | {id: string}
      | TopicDescriptionPushTransferResponse
      | TopicDescriptionMqttTransferResponse
    >(
      API.fetchWithHeaders(
        "topics/descriptions",
        "POST",
        true,
        topicDescription,
      ),
      bodyAsSpecifics,
    );
  },

  /**
   * Attempts to get all topics' descriptions.
   * @function
   */
  getTopicDescriptions: async (): Promise<
    Resolution<Array<TopicDescriptionInfo>>
  > => {
    return API.resolveFetch<Array<TopicDescriptionInfo>>(
      API.fetchWithHeaders("topics/descriptions", "GET", true),
    );
  },

  /**
   * Attempts to get a single topic's description.
   * @function
   * @param id - the id of the topic description
   */
  getTopicDescription: async (
    id: string,
  ): Promise<Resolution<TopicDescription>> => {
    return API.resolveFetch<TopicDescription>(
      API.fetchWithHeaders("topics/descriptions/" + id, "GET", true),
    );
  },

  /**
   * Attempts to get a topic's dataset info.
   * @function
   * @param id - the id of the topic description
   */
  getDatasetDescription: async (
    id: string,
  ): Promise<Resolution<DatasetDescription>> => {
    return API.resolveFetch<DatasetDescription>(
      API.fetchWithHeaders(`topics/descriptions/${id}/datasets`, "GET", true),
    );
  },

  /**
   * Attempts to update a topic description.
   * @function
   * @param id - the id of the topic description
   * @param topicDescription - the details about the topic description
   */
  updateTopicDescription: async (
    id: string,
    topicDescription: OutgoingTopicDescription,
  ): Promise<Resolution<null>> => {
    return await API.resolveFetch<null>(
      API.fetchWithHeaders(
        "topics/descriptions/" + id,
        "PUT",
        true,
        topicDescription,
      ),
    );
  },

  /**
   * Attempts to update the status of a topic description
   * @function
   * @param id - the id of the topic description
   * @param status - the new status
   */
  updateStatus: async (
    id: string,
    status: OutgoingTopicDescription["status"],
  ): Promise<Resolution<{health: TopicDescriptionInfo["health"]}>> => {
    return await API.resolveFetch<{health: TopicDescriptionInfo["health"]}>(
      API.fetchWithHeaders(
        `topics/descriptions/${id}/change_status`,
        "PUT",
        true,
        {
          status: status,
        },
      ),
    );
  },

  /**
   * Attempts to test a topic connection
   * @function
   * @param id - id of the topic
   * @param abortController - controller to handle aborting of requests
   */
  testTopicConnection: async (
    id: string,
    abortController: AbortController,
  ): Promise<Resolution<null>> => {
    return API.resolveFetch<null>(
      API.fetchWithHeaders(
        "topics/descriptions/test/" + id,
        "POST",
        true,
        undefined,
        abortController,
      ),
    );
  },

  /**
   * Attempts to get all application domains
   * @function
   */
  getAppDomains: async (): Promise<
    Resolution<Array<Omit<ApplicationDomain, "data_streams">>>
  > => {
    return API.resolveFetch<Array<Omit<ApplicationDomain, "data_streams">>>(
      API.fetchWithHeaders("apps/domains", "GET", true),
    );
  },

  /**
   * Attempts to get a single application domain.
   * @function
   * @param id - the id of the application domain
   */
  getAppDomain: async (id: string): Promise<Resolution<ApplicationDomain>> => {
    return API.resolveFetch<ApplicationDomain>(
      API.fetchWithHeaders("apps/domains/" + id, "GET", true),
    );
  },

  /**
   * Attempts to create a application domain.
   * @function
   * @param appDomain - the details about the application domain
   */
  createAppDomain: async (
    appDomain: ApplicationDomainCreate,
  ): Promise<Resolution<{id: string}>> => {
    return await API.resolveFetch<{id: string}>(
      API.fetchWithHeaders("apps/domains", "POST", true, appDomain),
    );
  },

  /**
   * Attempts to delete a specific application domain
   * @function
   * @param id - the id of the application domain
   */
  deleteAppDomain: async (id: string): Promise<Resolution<null>> => {
    return await API.resolveFetch<null>(
      API.fetchWithHeaders("apps/domains/" + id, "DELETE", true),
    );
  },

  /**
   * Attempts to update an application domain.
   * @function
   * @param id - the id of the application domain
   * @param appDomainDetails - the details about the application domain
   */
  updateAppDomain: async (
    id: string,
    appDomainDetails: Partial<Pick<ApplicationDomain, "name" | "description">>,
  ): Promise<Resolution<null>> => {
    return await API.resolveFetch<null>(
      API.fetchWithHeaders(
        "apps/domains/" + id,
        "PATCH",
        true,
        appDomainDetails,
      ),
    );
  },

  /**
   * Attempts to get all available applications (for use in domains).
   * @function
   * @param assigned - whether to get assigned or unassigned applications
   */
  getAssignedApps: async (
    assigned: boolean,
  ): Promise<Resolution<Application[]>> => {
    return await API.resolveFetch<Application[]>(
      API.fetchWithHeaders(`apps?assigned=${assigned}`, "GET", true),
    );
  },

  /**
   * Attempts to get all applications (for use in domains).
   * @function
   */
  getApps: async (): Promise<Resolution<Application[]>> => {
    return await API.resolveFetch<Application[]>(
      API.fetchWithHeaders(`apps`, "GET", true),
    );
  },

  /**
   * Attempts to create a new application (for use in domains).
   * @function
   */
  createApp: async (name: string): Promise<Resolution<{id: string}>> => {
    return await API.resolveFetch<{id: string}>(
      API.fetchWithHeaders("apps", "POST", true, {name}),
    );
  },

  /**
   * Attempts to get all applications from the portal backend.
   * @function
   */
  getApplications: async (): Promise<Resolution<TileApplications>> => {
    return API.resolveFetch<TileApplications>(
      API.fetchWithHeaders("apps", "GET", true, undefined, undefined, "portal"),
    );
  },

  /**
   * Attempts to delete a specific topic description
   * @function
   * @param id - the id of the topic description
   */
  deleteTopic: async (id: string): Promise<Resolution<null>> => {
    return await API.resolveFetch<null>(
      API.fetchWithHeaders("topics/descriptions/" + id, "DELETE", true),
    );
  },

  /**
   * Attempts to get the content of a topic folder.
   * @function
   */
  getTopicFolderContent: async (
    topicId: string,
  ): Promise<Resolution<TopicFolderContent>> => {
    return API.withFileAccessToken(topicId, (token) =>
      API.resolveFetch<TopicFolderContent>(
        API.fetchWithHeaders(
          "folder_topics/" + topicId,
          "GET",
          true,
          undefined,
          undefined,
          "file_store",
          [
            {
              name: "File-Access-Token",
              value: token,
            },
          ],
        ),
      ),
    );
  },

  /**
   * Attempts to get the content of a draft folder.
   * @function
   */
  getDraftFolderContent: async (
    topicId: string,
    commitId: string,
  ): Promise<Resolution<DraftFolderContent>> => {
    return API.withFileAccessToken(topicId, (token) =>
      API.resolveFetch<DraftFolderContent>(
        API.fetchWithHeaders(
          "folder_topics/" + topicId + "/drafts/" + commitId,
          "GET",
          true,
          undefined,
          undefined,
          "file_store",
          [
            {
              name: "File-Access-Token",
              value: token,
            },
          ],
        ),
      ),
    );
  },

  /**
   * Attempts to create a draft folder.
   * @function
   */
  createDraftFolder: async (
    topicId: string,
  ): Promise<Resolution<CreateDraftFolderResponse>> => {
    return API.withFileAccessToken(topicId, (token) =>
      API.resolveFetch<CreateDraftFolderResponse>(
        API.fetchWithHeaders(
          "folder_topics/" + topicId + "/drafts",
          "POST",
          true,
          undefined,
          undefined,
          "file_store",
          [
            {
              name: "File-Access-Token",
              value: token,
            },
          ],
        ),
      ),
    );
  },

  /**
   * Attempts to delete a draft folder.
   * @function
   */
  deleteDraftFolder: async (
    topicId: string,
    commitId: string,
  ): Promise<Resolution<null>> => {
    return API.withFileAccessToken(topicId, (token) =>
      API.resolveFetch<null>(
        API.fetchWithHeaders(
          "folder_topics/" + topicId + "/drafts/" + commitId,
          "DELETE",
          true,
          undefined,
          undefined,
          "file_store",
          [
            {
              name: "File-Access-Token",
              value: token,
            },
          ],
        ),
      ),
    );
  },

  /**
   * Attempts to publish a draft folder.
   * @function
   */
  publishDraftFolder: async (
    topicId: string,
    commitId: string,
  ): Promise<Resolution<null>> => {
    return API.withFileAccessToken(topicId, (token) =>
      API.resolveFetch<null>(
        API.fetchWithHeaders(
          "folder_topics/" + topicId + "/drafts/" + commitId + "/publish",
          "POST",
          true,
          undefined,
          undefined,
          "file_store",
          [
            {
              name: "File-Access-Token",
              value: token,
            },
          ],
        ),
      ),
    );
  },

  /**
   * Attempts to delete a file from a draft folder.
   * @function
   */
  deleteFileFromDraftFolder: async (
    topicId: string,
    commitId: string,
    fileName: string,
  ): Promise<Resolution<null>> => {
    return API.withFileAccessToken(topicId, (token) =>
      API.resolveFetch<null>(
        API.fetchWithHeaders(
          "folder_topics/" +
            topicId +
            "/drafts/" +
            commitId +
            "/files/" +
            fileName,
          "DELETE",
          true,
          undefined,
          undefined,
          "file_store",
          [
            {
              name: "File-Access-Token",
              value: token,
            },
          ],
        ),
      ),
    );
  },

  /**
   * Attempts to upload a file into the general file store (user_files).
   * The response will not be returned but execute the according callback function.
   * Instead, the request and file reader are returned, which can be used to abort the post.
   * @function
   */
  uploadFile: (
    file: File,
    onProgress: (e: ProgressEvent) => void,
    onLoad: (e: ProgressEvent) => void,
    onFail: (e: ProgressEvent) => void,
  ): {xhr: XMLHttpRequest; reader: FileReader} => {
    const reader = new FileReader();
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", onProgress);
    xhr.addEventListener("load", onLoad);
    xhr.addEventListener("error", onFail);
    xhr.addEventListener("timeout", onFail);
    xhr.open("POST", externalBackends["file_store"] + "user_files/upload");
    xhr.setRequestHeader("Authorization", "Bearer " + keycloak.token);
    xhr.setRequestHeader("Filename", encodeURIComponent(file.name));
    xhr.setRequestHeader("Content-Type", "octet-stream");
    xhr.overrideMimeType("text/plain; charset=x-user-defined-binary");
    reader.onload = (e) => {
      if (e.target) xhr.send(e.target.result);
    };
    reader.onerror = onFail;
    reader.readAsArrayBuffer(file);
    return {xhr, reader};
  },

  /**
   * Attempts to get an access token to upload files into a topic.
   * @function
   */
  getFileAccessToken: async (
    topicId: string,
    abortController: AbortController,
  ): Promise<Resolution<{token: string}>> => {
    return API.resolveFetch<{token: string}>(
      API.fetchWithHeaders(
        "topics/file_access_token/" + topicId,
        "GET",
        true,
        undefined,
        abortController,
      ),
    );
  },

  /**
   * Attempts to upload a file into a data topic in the same manner as uploadFile.
   * @function
   */
  uploadFileToTopic: (
    file: File,
    onProgress: (e: ProgressEvent) => void,
    onLoad: (e: ProgressEvent) => void,
    onFail: (e: ProgressEvent) => void,
    topicId: string,
    token: string,
  ): {xhr: XMLHttpRequest; reader: FileReader} => {
    const reader = new FileReader();
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", onProgress);
    xhr.addEventListener("load", onLoad);
    xhr.addEventListener("error", onFail);
    xhr.addEventListener("timeout", onFail);
    xhr.open(
      "POST",
      `${externalBackends["file_store"]}files/topics/upload/${topicId}`,
    );
    xhr.setRequestHeader("Authorization", "Bearer " + keycloak.token);
    xhr.setRequestHeader("Filename", encodeURIComponent(file.name));
    xhr.setRequestHeader("Content-Type", "octet-stream");
    xhr.setRequestHeader("File-Access-Token", token);
    xhr.overrideMimeType("text/plain; charset=x-user-defined-binary");
    reader.onload = (e) => {
      if (e.target) xhr.send(e.target.result);
    };
    reader.onerror = onFail;
    reader.readAsArrayBuffer(file);
    return {xhr, reader};
  },

  /**
   * Attempts to get all DM Mitglieder from the keycloak sidecar.
   * @function
   */
  getUsersEligibleForTransfer: async (): Promise<Resolution<User[]>> => {
    const tokenResponse = await API.getUsersLookupToken();
    if (!tokenResponse.success || !tokenResponse.payload)
      return tokenResponse as Resolution<never>;
    return API.resolveFetch<User[]>(
      API.fetchWithHeaders(
        "?role=DM Mitglied",
        "GET",
        false,
        undefined,
        undefined,
        "keycloak_sidecar",
        [
          {
            name: "Users-Lookup-Access-Token",
            value: tokenResponse.payload?.token,
          },
        ],
      ),
    );
  },

  /**
   * Attempts to get an access token to request users from the keycloak sidecar.
   * @function
   */
  getUsersLookupToken: async (): Promise<Resolution<{token: string}>> => {
    return API.resolveFetch<{token: string}>(
      API.fetchWithHeaders("users_lookup_access_token", "GET", true),
    );
  },

  /**
   * Attempts to transfer the ownership of a topic to a different user.
   * @function
   */
  transferOwnershipOfTopic: async (
    topicId: string,
    userId: string,
  ): Promise<Resolution<null>> => {
    return API.resolveFetch<null>(
      API.fetchWithHeaders(
        `topics/descriptions/change_owner/${topicId}`,
        "PATCH",
        true,
        {user_id: userId},
      ),
    );
  },

  /**
   * Attempts to get all connectors.
   * @function
   */
  getConnectors: async (): Promise<Resolution<Connector[]>> => {
    return API.resolveFetch<Connector[]>(
      API.fetchWithHeaders("topics/connectors", "GET", true),
    );
  },

  /**
   * Creates the websocket to receive updates about connectors.
   * @function
   */
  initializeConnectorsWebSocket: () => {
    const url = API.url.startsWith("http")
      ? API.url
      : location.origin + API.url;
    return new WebSocket(
      url.replace("http", "ws") +
        `topics/connectors/push_status?access_token=${keycloak.token || ""}`,
    );
  },

  /**
   * Attempts to delete a specific application
   * @function
   * @param id - the id of the application
   */
  deleteApplication: async (id: string): Promise<Resolution<null>> => {
    return await API.resolveFetch<null>(
      API.fetchWithHeaders("apps/" + id, "DELETE", true),
    );
  },

  /**
   * Attempts to create a new data stream.
   * @function
   */
  createDataStream: async (
    domainId: string,
    streamName: string,
    topicIds: string[],
  ): Promise<Resolution<DataStream>> => {
    return API.resolveFetch<DataStream>(
      API.fetchWithHeaders(
        `apps/domains/${domainId}/data_streams`,
        "POST",
        true,
        {name: streamName, topic_ids: topicIds},
      ),
    );
  },

  /**
   * Attempts to get the most recent data topics
   * @function
   * @param limit - how many topics to request
   */
  getRecentTopics: async (
    limit: number,
  ): Promise<Resolution<RecentTopicDescription[]>> => {
    return await API.resolveFetch<RecentTopicDescription[]>(
      API.fetchWithHeaders(
        "topics/descriptions/recent?limit=" + limit,
        "GET",
        true,
      ),
    );
  },

  /**
   * Attempts to get the number of active data topics
   * @function
   */
  getNumberOfActiveTopics: async (): Promise<Resolution<ActiveTopicCount>> => {
    return await API.resolveFetch<ActiveTopicCount>(
      API.fetchWithHeaders(
        "topics/descriptions/count?health=ACTIVE_NO_ERROR&visibility=PUBLIC",
        "GET",
        true,
      ),
    );
  },

  /**
   * Attempts to add more topics to a data stream.
   * @function
   */
  addTopicsToDataStream: async (
    domainId: string,
    streamId: string,
    topicIds: string[],
  ): Promise<Resolution<null>> => {
    return API.resolveFetch<null>(
      API.fetchWithHeaders(
        `apps/domains/${domainId}/data_streams/${streamId}`,
        "POST",
        true,
        {topic_ids: topicIds},
      ),
    );
  },

  /**
   * Attempts to delete a data stream.
   * @function
   */
  deleteDataStream: async (
    domainId: string,
    streamId: string,
  ): Promise<Resolution<null>> => {
    return API.resolveFetch<null>(
      API.fetchWithHeaders(
        `apps/domains/${domainId}/data_streams/${streamId}`,
        "DELETE",
        true,
      ),
    );
  },
};

export default API;
export {type Resolution};
